import React, { useState } from 'react';

import { useTextSelection } from '../hooks/useTextSelection';
import { COPY_EXCLUDED_ELEMENT_CLASS_NAME } from '../helpers/handleCopyFormattedText';

import ToolTip from '../design-system/ToolTip/ToolTip';
import SuccessAlert from '../design-system/SuccessAlert/SuccessAlert';
import SelectionPopup from '../components/SelectionPopup/SelectionPopup';
import DiscussLineIcon from '../design-system/Icons/DiscussLineIcon';
import ShareFeedbackModal from '../components/ShareFeedbackModal/ShareFeedbackModal';
import { ButtonIcon } from '../design-system';

// wrap with SelectionProvider
export function withTextSelection(WrappedComponent) {
    return function WithTextSelection(props) {
        const {
            contentPieceId,
            feedbackQuoteDisabled = false,
            feedbackTarget,
            feedbackTargetId,
            feedbackAllowed,
            customFeedbackButtonType = null,
            customToolTipPosition = null,
            feedbackCount = 0,
            onSuccessFeedbackShare,
            feedbackSuccessAlertMessage,
            selectionPopupTopOffset = 0,
            ...restProps
        } = props;

        const [successAlert, setSuccessAlert] = useState(null);

        const {
            containerRef,
            clearSelection,
            openFeedbackWithQuote,
            closeFeedbackModal,
            feedbackModal,
            selection: localSelection,
        } = useTextSelection({
            fieldId: contentPieceId,
            disabled: feedbackQuoteDisabled,
        });
        const isPopupVisible = localSelection.isActive;

        const onSuccess = async (data) => {
            onSuccessFeedbackShare && (await onSuccessFeedbackShare(data));

            clearSelection();
        };

        const top =
            localSelection?.position?.top -
                containerRef.current?.offsetTop -
                42 -
                (selectionPopupTopOffset || 0) || 0;
        const quote = feedbackModal.quotedText || '';

        const openLogFeedbackModal = () => {
            if (!localSelection.isActive) {
                window.getSelection()?.removeAllRanges();
            }
            openFeedbackWithQuote();
        };

        const logFeedbackTrigger = feedbackAllowed && (
            <div className="relative">
                <ToolTip
                    text={
                        <span className={`${COPY_EXCLUDED_ELEMENT_CLASS_NAME} select-none`}>
                            Log Feedback
                        </span>
                    }
                    position={customToolTipPosition || 'top-right'}
                    shift="-3px"
                    useInternalHoverStateToShowToolTip
                >
                    <ButtonIcon
                        type={customFeedbackButtonType || 'link'}
                        size="xs"
                        icon={DiscussLineIcon}
                        onClick={openLogFeedbackModal}
                    />
                </ToolTip>

                {!!feedbackCount && (
                    <span className="absolute top-[1px] right-[-3px] min-w-[10px] w-fit h-[10px] p-0.5 rounded-full bg-red-500 text-[8px] text-white font-medium font-body inline-flex justify-center items-center">
                        {feedbackCount}
                    </span>
                )}
            </div>
        );

        return (
            <div ref={containerRef} className="relative" data-field-id={contentPieceId}>
                <WrappedComponent
                    {...restProps}
                    feedbackAllowed={feedbackAllowed}
                    logFeedbackTrigger={logFeedbackTrigger}
                />

                {isPopupVisible && (
                    <SelectionPopup
                        position={{ top, left: 0 }}
                        onQuoteClick={openFeedbackWithQuote}
                        onClickOutside={clearSelection}
                    />
                )}

                {feedbackModal.isOpen && (
                    <ShareFeedbackModal
                        target={feedbackTarget}
                        targetId={feedbackTargetId}
                        contentPieceId={contentPieceId}
                        quote={quote}
                        state="empty"
                        variant="primary"
                        setSuccessAlert={setSuccessAlert}
                        onSuccessFeedbackShare={onSuccess}
                        successAlertMessage={feedbackSuccessAlertMessage}
                        onClose={() => closeFeedbackModal({ keepSelection: false })}
                        onCancel={() => closeFeedbackModal({ keepSelection: true })}
                    />
                )}

                <SuccessAlert successAlert={successAlert} setSuccessAlert={setSuccessAlert} />
            </div>
        );
    };
}
